import React, { useEffect, useRef, memo } from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import styles from './DocumentContent.module.scss';
import { DocumentContentProps } from './types';
import { TemplatePlaceholdersBlock } from './components/TemplatePlaceholdersBlock';
import { ProgressOverlay } from '../../../../common/progress-overlay/ProgressOverlay';
import { ActionButtons } from '../action-buttons/ActionButtons';
import ConfirmModal from '../../../../common/confirm-modal/ConfirmModal';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../../../../common/constants/routes';
import InfoIcon from '@mui/icons-material/Info';
import { Editor } from '@tinymce/tinymce-react';
import { sanitizeRedirectUrl } from '../../../../common/helpers/sanitize';
import { useDocumensoRedirect } from '../../../../common/hooks/useDocumensoRedirect';

const DocumentContent = ({
  documentHTML,
  downloadDocument,
  generateAISummary,
  signDocument,
  editDocument,
  createTemplate,
  deleteDocument,
  saveDocument,
  setDocumentHTML,
  contentEditable,
  setContentEditable,
  doc,
  templateCreationEnabled,
  templatePlaceholders,
  createTemplateFromDoc,
  goBack,
  showConfirmationModal,
  setShowConfirmationModal,
  documentHTMLId,
  addTemplatePlaceholderRow,
  isImage,
  fileLoading,
  fileBlobURL,
  pdfBlobMode,
  setPDFBlobMode,
  templateCreationStep,
  setTemplateCreationStep,
  deletePlaceholders,
  deletePlaceholdersMessageVisible,
  setDeletePlaceholdersMessageVisible,
  typeLabels,
  categoryArray,
  workflowsArray,
  setCategory,
  setDocumentTypeLabel,
  setDocumentWorkflowLabel,
  setCustomLabel,
  category,
  documentTypeLabel,
  documentWorkflowLabel,
  templateName,
  setTemplateName,
  customLabel,
  placeholderCheckUncheck,
}: DocumentContentProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const contentBlockRef = useRef<HTMLDivElement | null>(null);
  const [params] = useSearchParams();
  const editDocumentParam = params.get('editDocument');
  const { redirectToDocumenso, isLoading: False } = useDocumensoRedirect();
  const { t } = useTranslation();
  const editorPlugins =
    import.meta.env.VITE_ENV_TYPE === 'PROD'
      ? [
          'anchor',
          'autolink',
          'charmap',
          'codesample',
          'emoticons',
          'link',
          'lists',
          'searchreplace',
          'table',
          'visualblocks',
          'wordcount',
        ]
      : [
          'anchor',
          'autolink',
          'charmap',
          'codesample',
          'emoticons',
          'link',
          'lists',
          'searchreplace',
          'table',
          'visualblocks',
          'wordcount',
          'code',
        ];
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        contentEditable &&
        contentBlockRef.current &&
        !contentBlockRef.current.contains(event.target as Node) /*&&*/
        //(!popperNode || !popperNode.contains(event.target as Node)) // Ahora ignora correctamente los clics en el menú
      ) {
        /* if (
                    targetElement.closest('.MuiMenuItem-root') || // Ignore clicks on MenuItem
                    targetElement.closest('.MuiSelect-root') // Ignore clicks on Select
                ) {
                  return;
                }*/
        setTimeout(() => {
          //setShowConfirmationModal(true);
        }, 0);
      }
    };

    //if (contentEditable) document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contentEditable]);

  return (
    <>
      <Box className={styles.container} ref={contentBlockRef}>
        <Box className={styles.topBar}>
          <Box className={styles.headerActions}>
            {doc?.is_flattened && (
              <Tooltip title={t('messages.flattenedPDFMessage')} arrow placement='top'>
                <InfoIcon color='primary' fontSize='small' style={{ marginRight: '8px' }} />
              </Tooltip>
            )}
            {doc && doc?.signing_doc_id && !isImage && !doc?.is_flattened && (
              <Button
                type='button'
                className={
                  'btn-default btn-action-basic btn-small btn-green' +
                  (doc.status === 'PENDING' ? ' btn-blue' : '')
                }
                role='button'
                variant='contained'
                onClick={() =>
                  redirectToDocumenso(
                    `${ROUTES.DOCUMENTS}/${doc.signing_doc_id}?view=true&backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}`
                  )
                }
                style={{ marginRight: '8px' }}
              >
                {doc.status === 'COMPLETED'
                  ? t('buttons.signedDocument')
                  : t('buttons.pendingSigning')}
              </Button>
            )}
            {doc &&
              'du_filetype' in doc &&
              doc.du_filetype[0] === 'pdf' &&
              documentHTML &&
              !isImage &&
              !doc?.is_flattened && (
                <Button
                  type='button'
                  className='btn-default btn-action-basic btn-small'
                  role='button'
                  variant='contained'
                  onClick={() => {
                    pdfBlobMode
                      ? (() => {
                          setPDFBlobMode(false);
                          //setContentEditable(true);
                          //editDocument();
                        })()
                      : (() => {
                          setPDFBlobMode(true);
                          setContentEditable(false);
                          navigate(window.location.pathname + '?initialTab=0');
                        })();
                  }}
                  style={{ marginRight: '8px' }}
                >
                  {pdfBlobMode ? t('buttons.viewLatestVersion') : t('buttons.viewOriginalPDF')}
                </Button>
              )}
            <ActionButtons
              isImage={isImage}
              handleSaveDocument={saveDocument}
              handleAISummaryGeneration={generateAISummary}
              handleDocumentDownload={downloadDocument}
              handleSignDocument={doc && doc.signing_doc_id ? null : signDocument}
              setEditingDocument={setContentEditable}
              handleEditDocument={() => {
                if (!doc?.is_flattened) {
                  editDocument();
                  setContentEditable(true);
                  setPDFBlobMode(false);
                }
              }}
              handleCreateTemplate={
                !doc?.is_flattened
                  ? doc && 'contract_generation' in doc
                    ? null
                    : () => {
                        createTemplate();
                        setPDFBlobMode(false);
                      }
                  : null
              }
              handleDeleteDocument={deleteDocument}
              buttonSaveDisabled={!contentEditable || pdfBlobMode || templateCreationEnabled}
              handleCreateTemplateMainBtn={true}
              creatingTemplateEnabled={templateCreationEnabled}
              doc={doc}
              isDocumentContentTab={true}
              cancelEditMode={() => {
                setContentEditable(false);
                setPDFBlobMode(false);
              }}
            />
          </Box>
        </Box>
        <Box className={styles.mainContent}>
          {templateCreationEnabled && (
            <TemplatePlaceholdersBlock
              placeholderCheckUncheck={placeholderCheckUncheck}
              deletePlaceholdersMessageVisible={deletePlaceholdersMessageVisible}
              setDeletePlaceholdersMessageVisible={setDeletePlaceholdersMessageVisible}
              templateCreationStep={templateCreationStep}
              setTemplateCreationStep={setTemplateCreationStep}
              templatePlaceholders={templatePlaceholders}
              createTemplate={createTemplateFromDoc}
              goBack={goBack}
              documentHTMLId={documentHTMLId}
              deletePlaceholders={deletePlaceholders}
              addRow={addTemplatePlaceholderRow}
              typeLabels={typeLabels}
              workflowsArray={workflowsArray}
              categoryArray={categoryArray}
              setCategory={setCategory}
              setDocumentTypeLabel={setDocumentTypeLabel}
              setDocumentWorkflowLabel={setDocumentWorkflowLabel}
              setCustomLabel={setCustomLabel}
              category={category}
              documentTypeLabel={documentTypeLabel}
              documentWorkflowLabel={documentWorkflowLabel}
              templateName={templateName}
              setTemplateName={setTemplateName}
              customLabel={customLabel}
            />
          )}
          <div className={`${styles.main_container} ${templateCreationEnabled ? styles.w_50 : ''}`}>
            {doc && 'du_filetype' in doc && doc.du_filetype[0] === 'pdf' && pdfBlobMode && (
              // eslint-disable-next-line jsx-a11y/iframe-has-title
              <iframe src={fileBlobURL} className={styles.frame} />
            )}
            <div
              id='editor'
              className={
                'main-editor-container' +
                (doc &&
                'du_filetype' in doc &&
                doc.du_filetype[0] === 'pdf' &&
                (documentHTML === undefined || documentHTML === null || pdfBlobMode)
                  ? ' hidden'
                  : '')
              }
            >
              {isImage ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                >
                  <img
                    src={fileBlobURL}
                    alt=''
                    style={{ objectFit: 'contain', height: 'auto', maxHeight: '90%' }}
                  />
                </div>
              ) : (
                <Editor
                  tinymceScriptSrc={'/tinymce/tinymce.min.js'}
                  licenseKey='gpl'
                  disabled={
                    (!contentEditable && editDocumentParam !== 'true') || templateCreationEnabled
                  }
                  init={{
                    content_style:
                      '#tinymce {max-width: 210mm; margin-left: auto; margin-right: auto; padding:2.54cm; box-shadow: 0 0 3px rgba(0, 0, 0, 0.3)} img {display: none !important;} tox-promotion {display: none !important;}',
                    menubar: 'edit insert view format table tools',
                    plugins: editorPlugins,
                    toolbar: false,
                    /*'undo redo| blocks fontfamily fontsize | bold italic underline strikethrough | link table | spellcheckdialog a11ycheck typography uploadcare | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat'*/
                    ai_request: (request: any, respondWith: any) =>
                      respondWith.string(() =>
                        Promise.reject('See docs to implement AI Assistant')
                      ),
                    uploadcare_public_key: 'b97265d9de273c24383a',
                    // XSS prevention: strip <script> tags and on* event attributes from pasted/loaded content
                    invalid_elements: 'script,object,embed,applet,iframe,form,input,button',
                    extended_valid_elements: '',
                    cleanup_on_startup: true,
                    convert_unsafe_embeds: true,
                    sandbox_iframes: true,
                    // Restrict link protocols to safe schemes only
                    link_assume_external_targets: false,
                    allow_script_urls: false,
                  }}
                  value={documentHTML}
                  onEditorChange={(value) => {
                    if (value) {
                      setDocumentHTML(value);
                    } else {
                      setDocumentHTML('');
                    }
                  }}
                  //initialValue='Welcome to TinyMCE!'
                />
                /*<JoditEditor
                                  ref={editor}
                                  config={htmlEditorConfig}
                                  value={documentHTML}
                                  onBlur={(newContent) => setDocumentHTML(newContent)} // preferred to use only this option to update the content for performance reasons
                                />*/
              )}
            </div>
          </div>
        </Box>
        {showConfirmationModal && (
          <ConfirmModal
            open={showConfirmationModal}
            onClose={() => {
              saveDocument();
              setShowConfirmationModal(false);
            }}
            onConfirm={() => {
              setContentEditable(false);
              setShowConfirmationModal(false);
            }}
          />
        )}
      </Box>
      {fileLoading && <ProgressOverlay className='position-absolute' />}
    </>
  );
};

export default memo(DocumentContent);
